import React from 'react';
import Icon from '../../../components/AppIcon';

const BottleneckIdentification = ({ bottlenecks }) => {
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          border: 'border-error/30',
          icon: 'AlertOctagon'
        };
      case 'high':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          border: 'border-error/30',
          icon: 'AlertTriangle'
        };
      case 'medium':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          border: 'border-warning/30',
          icon: 'AlertCircle'
        };
      case 'low':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          border: 'border-accent/30',
          icon: 'Info'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          border: 'border-border',
          icon: 'AlertCircle'
        };
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="mb-6">
        <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1">
          Workflow Bottlenecks
        </h3>
        <p className="caption text-xs md:text-sm text-muted-foreground">
          Identified friction points requiring optimization
        </p>
      </div>

      <div className="space-y-3">
        {bottlenecks?.slice(0, 6)?.map((bottleneck, index) => {
          const styles = getSeverityStyles(bottleneck?.severity);
          return (
            <div 
              key={index}
              className={`p-3 rounded-md border ${styles?.border} ${styles?.bg} transition-smooth hover:shadow-sm`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Icon name={styles?.icon} size={18} color={`var(--color-${bottleneck?.severity === 'critical' || bottleneck?.severity === 'high' ? 'error' : bottleneck?.severity === 'medium' ? 'warning' : 'accent'})`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-heading text-sm font-medium text-foreground">
                      {bottleneck?.workflowName}
                    </p>
                    <span className={`caption text-xs font-bold ${styles?.text} uppercase`}>
                      {bottleneck?.severity}
                    </span>
                  </div>
                  <p className="caption text-xs text-muted-foreground mb-2">
                    {bottleneck?.stepName}
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                      <span className="data-text text-muted-foreground">
                        {formatDuration(bottleneck?.averageDurationSeconds)}
                      </span>
                    </div>
                    {bottleneck?.affectedUsersCount > 0 && (
                      <div className="flex items-center gap-1">
                        <Icon name="Users" size={12} color="var(--color-muted-foreground)" />
                        <span className="data-text text-muted-foreground">
                          {bottleneck?.affectedUsersCount} users
                        </span>
                      </div>
                    )}
                    {bottleneck?.department && (
                      <div className="flex items-center gap-1">
                        <Icon name="Building" size={12} color="var(--color-muted-foreground)" />
                        <span className="data-text text-muted-foreground">
                          {bottleneck?.department}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {bottlenecks?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3 opacity-50" />
          <p className="caption text-sm text-success font-medium">No bottlenecks identified</p>
          <p className="caption text-xs text-muted-foreground mt-1">All workflows operating efficiently</p>
        </div>
      )}
    </div>
  );
};

export default BottleneckIdentification;
